import cn from 'classnames';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useState } from 'react';

import type { IDataPoint, IQuotaTable, IQuotaTableCondition } from '@webapp/account/src/resources/respondents';

import { Collapse, Expand } from '@webapp/common/assets/icons';
import { RemoveBtn } from '@webapp/common/assets/icons/remove';
import { ActionsBar } from '@webapp/common/components/actions-bar';
import { AddRound, Button } from '@webapp/common/components/button';
import { Slider } from '@webapp/common/components/slider';

import css from './criteria.css';

interface IProps {
    data: IQuotaTable;
    point: IDataPoint & { optionsMapped: Record<string, string> };
    quota: number;
    isEditDisabled: boolean;
    onChange: (data: IQuotaTable) => void;
    onRemove: (code: string) => void;
    onAddCondition: (code: string) => void;
}

export const Criteria: FC<IProps> = ({ data, point, quota, isEditDisabled, onChange, onRemove, onAddCondition }) => {
    const [showSettings, setShowSettings] = useState(false);
    const [conditions, setConditions] = useState<Array<IQuotaTableCondition>>([]);
    const [freeQuota, setFreeQuota] = useState(0);

    useEffect(() => {
        setConditions(data.conditions);
    }, [data.conditions]);

    const toggleSettings = () => {
        setShowSettings((prev) => !prev);
    };

    return (
        <div className={css.criteria}>
            <div className={cn(css.criterion, css.content)}>
                <div className={css.title} onClick={toggleSettings}>
                    {point.title}
                </div>
                <ActionsBar
                    actions={[
                        {
                            title: !showSettings ? 'Развернуть' : 'Свернуть',
                            icon: !showSettings ? <Expand /> : <Collapse />,
                            onClick: toggleSettings,
                        },
                        {
                            disabled: isEditDisabled,
                            title: 'Удалить',
                            icon: <RemoveBtn color='white' />,
                            onClick: () => onRemove(data.point),
                        },
                    ]}
                    borderRadiusPosition='right'
                />
                <AddRound title='Добавить квоту' onClick={() => onAddCondition(data.point)} />
            </div>
            {showSettings && (
                <div className={cn(css.conditions, css.content)}>
                    {!isEditDisabled && (
                        <div className={css.header}>
                            Распределите&nbsp;<span className={css.answersCount}>{quota}</span>&nbsp;ответов
                            {freeQuota ? ` (осталось распределить ${freeQuota})` : ''}
                        </div>
                    )}
                    <div className={css.list}>
                        {conditions?.map((item) => {
                            const disabled = conditions.length === 1 || isEditDisabled;

                            return (
                                <div className={cn(css.condition, { [css.disabled]: disabled })} key={item.code}>
                                    <div>{point.optionsMapped[item.code]}</div>
                                    <Slider
                                        disabled={disabled}
                                        className={css.slider}
                                        controlClassName={css.control}
                                        fieldClassName={css.field}
                                        value={item.count || 0}
                                        max={item.count + freeQuota || 0}
                                        trackClassName='customized-track'
                                        onChange={(value) => {
                                            let newFreeQuota = quota;

                                            setConditions((prev) =>
                                                prev.map((c) => {
                                                    if (c.code === item.code) {
                                                        newFreeQuota -= value;
                                                        return { ...c, count: value };
                                                    }
                                                    newFreeQuota -= c.count;
                                                    return c;
                                                }),
                                            );

                                            setFreeQuota(newFreeQuota);
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    {!isEditDisabled && (
                        <Button
                            disabled={freeQuota > 0 || isEqual(conditions, data.conditions)}
                            color='blue'
                            onClick={() => {
                                onChange({ ...data, conditions });
                                setShowSettings(false);
                            }}
                            responsive={true}
                        >
                            Сохранить
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};
